import { ArrowLeft, RotateCcw } from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import {
  CodeBlock, StatusLine, auditButtonClass, runFacts, useAuditRun, useRerun, type AuditDetail,
} from '@/components/audit/RunDetail'
import { DetailPane, DetailSection, KeyValue } from '@/components/data/DetailPane'
import { LoadingRows } from '@/components/data/SearchInput'
import { StatTiles, type Tile } from '@/components/data/StatTiles'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'

type Part = 'parameters' | 'changes' | 'findings' | 'atlassian'

const PANEL_ID = 'audit-run-panel'
const TAB_PREFIX = 'audit-run-tab'

function partsOf(d: AuditDetail): { key: Part; label: string; count: number; body: unknown }[] {
  return [
    { key: 'parameters', label: 'Parameters', count: Object.keys(d.parameters ?? {}).length, body: d.parameters },
    { key: 'changes', label: 'Changes', count: d.changes?.length ?? 0, body: d.changes },
    { key: 'findings', label: 'Findings', count: d.findings?.length ?? 0, body: d.findings },
    {
      key: 'atlassian', label: 'Atlassian responses', count: Object.keys(d.atlassian_summary ?? {}).length,
      body: d.atlassian_summary,
    },
  ]
}

function RunBody({ d }: { d: AuditDetail }) {
  const [params, setParams] = useSearchParams()
  const parts = partsOf(d)
  const wanted = params.get('tab') as Part | null
  const tab: Part = parts.some((p) => p.key === wanted) ? wanted! : 'parameters'
  const active = parts.find((p) => p.key === tab)!
  const tiles: Tile[] = parts.map((p) => ({
    key: p.key, label: p.label, value: p.count,
    meta: p.key === 'parameters' ? 'keys sent' : p.count ? 'items' : 'none recorded',
  }))

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <StatTiles
          tiles={tiles}
          active={tab}
          onSelect={(k) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', k); return n }, { replace: true })}
          label="Run sections"
          idPrefix={TAB_PREFIX}
          panelId={PANEL_ID}
        />
        <div
          role="tabpanel"
          id={PANEL_ID}
          aria-labelledby={`${TAB_PREFIX}-${tab}`}
          className="min-h-0 flex-1 overflow-auto px-4 py-3"
        >
          <SectionBoundary key={tab} label={active.label}>
            {active.count === 0
              ? <p className="text-[13px] text-text-2">No {active.label.toLowerCase()} recorded for this run.</p>
              : <CodeBlock tone="text-foreground">{JSON.stringify(active.body, null, 2)}</CodeBlock>}
          </SectionBoundary>
        </div>
      </div>
      <aside aria-label="Run summary" className="w-[360px] shrink-0 overflow-y-auto border-l border-line bg-pane">
        <DetailPane eyebrow="Run" title={<span className="font-mono">{d.run_id}</span>} status={<StatusLine d={d} />}>
          <KeyValue items={runFacts(d).filter(([k]) => k !== 'Run')} />
          {d.error && (
            <DetailSection title="Error">
              <CodeBlock tone="text-danger">{d.error}</CodeBlock>
            </DetailSection>
          )}
          {d.warnings && d.warnings.length > 0 && (
            <DetailSection title={`Warnings · ${d.warnings.length}`}>
              <ul className="space-y-1 text-warn">
                {d.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </DetailSection>
          )}
        </DetailPane>
      </aside>
    </div>
  )
}

export default function AuditRun() {
  const { runId } = useParams<{ runId: string }>()
  const d = useAuditRun(runId)
  const rerun = useRerun()

  return (
    <Page
      title={d ? d.operation : 'Audit run'}
      context={(
        <Link to="/audit" className="inline-flex items-center gap-1 text-[12px] text-text-2 hover:text-foreground">
          <ArrowLeft aria-hidden className="size-3.5" /> Audit log
        </Link>
      )}
      actions={d
        ? (
          <button type="button" onClick={() => rerun(d)} className={auditButtonClass}>
            <RotateCcw aria-hidden className="size-3.5" /> Re-run
          </button>
        )
        : undefined}
    >
      {d === undefined && <LoadingRows label="Loading run" />}
      {d === null && <p className="px-4 py-4 text-[13px] text-text-2">Run not found.</p>}
      {d && <RunBody d={d} />}
    </Page>
  )
}
